import { QueryClient, useMutation, useQuery, useQueryClient, QueryKey } from 'react-query';
import {
    sendRequest,
    appendQueryParams,
    sendListRequest,
} from '../../utils/request';

export const queryClient = new QueryClient();

export type GetUsersParams = {
    name?: string;
    email?: string;
    organizationId?: string;
}

export class User {
    id?: string = undefined;
    name: string = "";
    email: string = "";
    organizationId: string = "";

    constructor(init?: Partial<User>) {
        if (!init) {
            return
        }

        Object.assign(this, init)
    }
}

export const useGetUser = createUseQuery('users', async (id: string) => {
    const reply = await sendRequest<Partial<User>>(`/api/users/${id}`);
    return new User(reply);
});

export const useGetUsers = createUseQuery('users', async (params?: GetUsersParams, offset = 0, limit = 50) => {
    let url = appendQueryParams(`/api/users`, {
        ...params,
        offset,
        limit,
    });
    const [reply, total] = await sendListRequest<Partial<User>>(url);
    return {
        data: reply.map(d => new User(d)),
        total,
    };
});

export const useChangePassword = () => {
    return useMutation<void, { code: string, description: string }[], { oldPassword: string, newPassword: string }>(
        async ({ oldPassword, newPassword }) => {
            await sendRequest(
                `/api/users/change-password`,
                {
                    body: {
                        oldPassword,
                        newPassword,
                    }
                }
            );
        }
    );
}

function createUseQuery<Params extends any[], Result>(queryKey: QueryKey, func: (...params: Params) => Promise<Result>) {
    const query = (...params: Params) => {
        return useQuery<Result>([queryKey, ...params], () => func(...params));
    }
    query.func = () => {
        const queryClient = useQueryClient();
        return (...params: Params) => {
            return queryClient.fetchQuery<Result>([queryKey, ...params], () => func(...params));
        };
    }
    return query;
}

export type Invitation = { id: string, email: string, organizationName: string, organizationId: string } | null;
export const useGetInvitation = createUseQuery<[string | null | undefined], Invitation>('invitation', async (invitationCode?: string | null | undefined) => {
    if (!invitationCode) {
        return null;
    }
    
    const reply = await sendRequest<Invitation>(`/api/invitations/${invitationCode}`);
    return reply ? {
        id: invitationCode,
        email: reply.email,
        organizationName: reply.organizationName,
        organizationId: reply.organizationId,
    } : null;
});

export type LoginParams = {
    email: string;
    password: string;
};

export const useLogin = () => {
    return useMutation<void, string, LoginParams>(
        async ({ email, password }) => {
            await sendRequest(
                `/api/users/login`,
                {
                    parseResponse: false,
                    body: {
                        username: email,
                        password,
                    },
                }
            );
        }
    );
}

export type ForgotForm = {
    email: string;
};

export const useForgotPassword = () => {
    return useMutation<boolean, string, ForgotForm>(
        async ({ email }) => {
            await sendRequest<void>(
                `/api/users/forget-password`,
                {
                    parseResponse: false,
                    body: {
                        email,
                    }
                }
            );
            return true;
        }
    );
};

export type SignUpParams = {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    organizationName: string;
    promoCode?: string;
    referralCode?: string;
    invitationCode?: string;
    recaptchaToken?: string;
    token?: string;
};

export const useSignUp = () => {
    return useMutation<boolean, string, SignUpParams>(
        async (data) => {
            const result = await sendRequest<{id: string}>(
                `/api/users/sign-up`,
                {
                    body: {
                        email: data.email,
                        password: data.password,
                        firstName: data.firstName,
                        lastName: data.lastName,
                        organizationName: data.organizationName,
                        promoCode: data.promoCode,
                        referralCode: data.referralCode,
                        recaptchaToken: data.recaptchaToken,
                        invitationCode: data.invitationCode,
                        token: data.token,
                    }
                }
            );

            return !!result?.id;
        }
    );
}

export type GoogleAuthParams = {
    token: string;
};

export type GoogleAuthResponse = {
    token: string;
}

export const useGoogleAuth = () => {
    return useMutation<GoogleAuthResponse, string, GoogleAuthParams>(
        async ({ token }): Promise<GoogleAuthResponse> => {
            const result = await sendRequest<GoogleAuthResponse>(
                `/api/google-auth/sign-in-or-sign-up`,
                {
                    parseResponse: true,
                    body: {
                        IdToken: token,
                    },
                }
            );

            if (!result) {
                throw new Error('Failed to authenticate with Google');
            }

            return result;
        }
    );
}
